import gsap from 'gsap';
import {
    chance,
    clamp,
    createCell,
    getEntityCategoryGroup,
    getEntityTypeGroup,
    getRandom,
    getTexture,
    getTextures,
    hypotenuse,
    log,
    random,
} from 'helpers';
import { Howl } from 'howler';
import PF from 'pathfinding';
import * as PIXI from 'pixi.js';
import { map as mapSetup } from 'setup';
import Map from '../Map/Map';

const pathFinder = new PF.BiBestFirstFinder({
    diagonalMovement: PF.DiagonalMovement.Always,
    heuristic: PF.Heuristic.chebyshev,
});

export default class Entity extends PIXI.Container {
    [index: string]: any;
    /** Amount of turns has been alive for */
    aliveTurns: number;
    /** Animated sprite with attacking textures */
    attackingSprite?: PIXI.AnimatedSprite;
    /** Base props passed by sub classes */
    baseProps: EntityBaseProps;
    /** Base setup passed by sub classes */
    baseSetup: EntityBaseSetup;
    /** Entity category */
    category: EntityCategory;
    /** Entity category group */
    categoryGroup: EntityCategoryGroup;
    /** Current position cell */
    cell: PIXI.Point;
    /** Current position cell name */
    cellName: string;
    /** The turn created at */
    createdTurn: number;
    /** If Map is following */
    following: boolean;
    /** Current state of the game */
    gameState: GameState;
    /** Sprite with glow texture */
    glowSprite?: PIXI.Sprite;
    /** Sprite with idle texture */
    idleSprite?: PIXI.Sprite;
    /** Amount of turns remaining to live */
    lifeRemaining?: number;
    /** Game Map */
    map: Map;
    /** Departure cell from current move */
    moveDeparture: PIXI.Point;
    /** Destination cell from current move */
    moveDestination?: PIXI.Point;
    /** Animated sprite with moving textures */
    movingSprite?: PIXI.AnimatedSprite;
    /** Mutation of the Entity */
    mutation?: EntityMutation;
    /** Name of the Entity (class + mutation) */
    name: string;
    /** Tween reference for the queued call */
    queueCall?: gsap.core.Tween;
    /** Role of the Entity */
    role?: EntityRole;
    /** Current size of the Entity (cells) */
    size: number;
    /** Looped sound, with volume based on position */
    sound: Howl | null;
    /** Position of looped sound */
    soundPosition: string | null;
    /** Current target Entity */
    target?: Entity | null;
    /** Entity category type */
    type: EntityType;
    /** Entity type group */
    typeGroup: EntityTypeGroup;

    /** Currently performing action */
    action: EntityAction = 'idle';
    /** Active state of Entity */
    active = true;
    /** Creation index if current Entity in its category and type */
    index = 0;
    /** Amount of kills */
    kills = 0;
    /** Amount of kills to target */
    killsTarget = 0;
    /** If target kills is reached */
    killsReached = false;
    /** Looped sound volume multiplier */
    soundVolume = 1;
    /** If Entity has engaged the target */
    targetEngaged = false;
    /** Anchor point of texture (0-1) */
    textureAnchor = new PIXI.Point(0.5, 0.5);
    /** Automatically rotate texture to movement direction */
    textureAutoRotate = true;
    /** Default texture rotation (radians) */
    textureRotation = 0;

    constructor(
        gameState: GameState,
        category: EntityCategory,
        type: EntityType,
        setup: EntityBaseSetup & EntitySetup,
        props: EntityBaseProps & EntityProps,
        state?: any
    ) {
        super();

        // Get groups and index
        this.categoryGroup = getEntityCategoryGroup(category);
        this.typeGroup = getEntityTypeGroup(type);
        this.index =
            ((gameState.entities[this.categoryGroup] as any)[this.typeGroup] || gameState.entities[this.categoryGroup])
                .created + 1;

        // Initialize
        this.baseProps = props;
        this.baseSetup = setup;
        this.createdTurn = gameState.turns;
        this.category = category;
        this.cell = createCell(props.x, props.y);
        this.gameState = gameState;
        this.killsTarget = props.killsTarget || 0;
        this.lifeRemaining = props.life;
        this.moveDeparture = this.cell.clone();
        this.mutation = props.mutation;
        this.name =
            this.constructor.name + (props.mutation ? `:${props.mutation}` : '') + (props.role ? `:${props.role}` : '');
        this.role = props.role;
        this.size = props.size || setup.size || 1;
        this.aliveTurns = gameState.turns;
        this.type = type;
        this.x = mapSetup.cells.size * ((props?.x || 0) + 0.5);
        this.y = mapSetup.cells.size * ((props?.y || 0) + 0.5);
        this.updateCell();

        // Check state
        if (state) {
            Object.entries(state).forEach(([key, value]) => {
                this[key] = value;
            });
        }

        // Add sprites
        void this.addAttackingSprite();
        void this.addIdleSprite();
        void this.addMovingSprite();

        // Add listeners
        this.addListener('added', this.handleAdded);
    }

    debugLog = (message: string) => {
        const { settings, turns } = this.gameState;
        if (
            (this.category === 'ant' && settings.debugAnts) ||
            (this.category === 'danger' && settings.debugDangers) ||
            (this.category === 'food' && settings.debugFoods) ||
            (this.category === 'nature' && settings.debugNature)
        ) {
            log(turns, this.name, message);
        }
    };

    getDebugRadius = () => {
        return this.getSearchDistance();
    };

    getSearchDistance = () => {
        return this.setup?.searchDistance;
    };

    /** Event for when added to the Map */
    handleAdded = () => {
        // Initialize
        this.map = this.parent!.parent as Map;
        this.updateCell();
        this.queue(this.init);
    };

    /** Event for when initialized, use for initialize subclass */
    init = () => {
        // Handle init
    };

    /** Stops the current action */
    stop = () => {
        this.setAction('idle');
        gsap.killTweensOf(this);
        this.queueCall?.kill();
        delete this.moveDestination;
        delete this.queueCall;
    };

    /** Stops and deactivates */
    deactivate = () => {
        this.stop();
        gsap.killTweensOf(this.scale);
        gsap.killTweensOf(this.glowSprite || {});
        gsap.killTweensOf(this.glowSprite?.scale || {});
        this.active = false;
        this.sound?.stop();
        this.targetEngaged = false;
        delete this.target;
    };

    /** Deactivates and removes from the Game */
    remove = () => {
        this.deactivate();
        this.gameState.removeEntity(null, null, this);
    };

    /** Destroys entirely */
    destroy = () => {
        // Unfollow
        if (this.following) {
            this.map.unfollowEntity();
        }

        // Destroy
        this.deactivate();
        super.destroy();
    };

    /** Deactivates and show death animation with indicator */
    die = () => {
        // Deactivate
        this.debugLog('Died');
        this.deactivate();

        // Animate out
        this.indicate('☠︎︎', 2);
        gsap.to(this.scale, {
            duration: 1,
            ease: 'power4.in',
            x: 0,
            y: 0,
            onComplete: this.remove,
        });
    };

    /** Highlights by scaling up and showing a glow */
    highlight = async () => {
        // Animate scale
        gsap.from(this.scale, {
            delay: 0.2,
            duration: 0.8,
            ease: 'power4.in',
            x: 2,
            y: 2,
        });

        // Animate zIndex
        gsap.from(this, {
            delay: 0.2,
            duration: 0.8,
            ease: 'power4.in',
            zIndex: 10,
        });

        // Show glow
        void this.showGlow();
    };

    /** Places indicator above */
    indicate = (value: string, size?: number) => {
        this.map.indicateEntity(this, value, size);
    };

    /** Queues a callback, with optional wait */
    queue = (callback?: CallableFunction | null, wait?: boolean) => {
        this.queueCall?.kill();
        if (callback) {
            const delay = random(0.01, 0.05) + (wait ? 1 : 0);
            this.queueCall = gsap.delayedCall(delay, () => {
                if (this.active && !this.destroyed) {
                    callback();
                }
            });
        }
    };

    playSound = (soundName: SoundName, volume = 1, loop?: boolean) => {
        // Play sound
        const vol = this.getDistancedSoundVolume(volume);
        const sound = this.gameState.sounds.play(soundName, vol);

        // Store if looped
        if (loop) {
            sound?.loop(true);
            this.sound = sound;
            this.soundVolume = volume;
        }
    };

    pauseSound = () => {
        if (this.sound?.playing()) {
            this.sound.pause();
        }
    };

    resumeSound = () => {
        if (this.sound && !this.sound.playing()) {
            this.sound.play();
        }
    };

    getDistancedSoundVolume = (baseVolume: number) => {
        if (this.gameState.map) {
            const { center, radius, zoom } = this.gameState.map.view;
            const distance = 1 - Math.min(1, this.getDistanceTo(center) / radius);
            return distance * zoom * baseVolume * this.gameState.settings.volume;
        }
        return 0;
    };

    /** Creates a snapshot of the current state */
    getSnapshot = () => {
        return {
            category: this.category,
            props: {
                ...this.baseProps,
                mutation: this.mutation,
                x: this.cell.x,
                y: this.cell.y,
            },
            state: {
                active: this.active,
                aliveTurns: this.aliveTurns,
                createdTurn: this.createdTurn,
                index: this.index,
                kills: this.kills,
                lifeRemaining: this.lifeRemaining,
                ...this.getSnapshotState(),
            },
            type: this.type,
        } as EntitySnapshot;
    };

    /** Gets the current state for getSnapshot */
    getSnapshotState = () => {
        return {};
    };

    /** Updates current Game state */
    updateGameState = (gameState: GameState) => {
        // Update state
        this.gameState = gameState;
        this.aliveTurns = gameState.turns - this.createdTurn;

        // Check life remaining
        if (this.lifeRemaining && --this.lifeRemaining <= 0) {
            this.wanderOff();
        }

        // Update sound
        if (this.sound?.playing()) {
            // Check map position and zoom
            const soundPosition = `${gameState.map.x}-${gameState.map.y}-${gameState.map.zoom}-${gameState.settings.volume}`;
            if (this.soundPosition !== soundPosition) {
                this.soundPosition = soundPosition;

                // Update volume
                const volume = this.getDistancedSoundVolume(this.soundVolume);
                this.sound.volume(volume);
            }
        }

        // Check target
        if (this.target && (!this.target.active || this.target.destroyed)) {
            this.target = null;
        }

        // Check finished
        if (gameState.finished) {
            this.deactivate();
        }
    };

    /** Add sprite with attacking textures */
    addAttackingSprite = async () => {
        if (this.baseProps.attackingTextures) {
            try {
                const textures = await getTextures(this.baseProps.attackingTextures);
                this.attackingSprite = new PIXI.AnimatedSprite(textures);
                this.attackingSprite.animationSpeed = (this.baseSetup.movingSpeed || 1) * 0.2;
                this.addSprite(this.attackingSprite);
            } catch (e) {
                console.warn('Failed to load attacking textures:', e);
            }
        }
    };

    /** Add sprite with idle texture */
    addIdleSprite = async () => {
        if (this.baseProps.idleTexture) {
            try {
                const texture = await getTexture(this.baseProps.idleTexture);
                this.idleSprite = new PIXI.Sprite(texture);
                this.addSprite(this.idleSprite);
            } catch (e) {
                console.warn('Failed to load idle texture:', e);
            }
        }
    };

    /** Add sprite with moving textures */
    addMovingSprite = async () => {
        if (this.baseProps.movingTextures) {
            try {
                const textures = await getTextures(this.baseProps.movingTextures);
                this.movingSprite = new PIXI.AnimatedSprite(textures);
                this.movingSprite.animationSpeed = (this.baseSetup.movingSpeed || 1) * 0.2;
                this.addSprite(this.movingSprite);
            } catch (e) {
                console.warn('Failed to load moving textures:', e);
            }
        }
    };

    /** Adds sprite with correct scale and rotation */
    addSprite = (sprite: PIXI.Sprite) => {
        // Pixi v8 removed BaseTexture; a loaded Texture exposes its pixel
        // dimensions directly via width/height.
        const { height: realHeight, width: realWidth } = sprite.texture;

        // Add sprite
        this.addChild(sprite);
        sprite.anchor.copyFrom(this.textureAnchor);
        sprite.rotation = this.textureRotation;

        // Scale to size
        const scale = (mapSetup.cells.size / Math.max(realHeight, realWidth)) * this.size;
        sprite.scale.set(scale);

        // Check action
        this.setAction(this.action);
    };

    /** Sets action and shows matching sprite */
    setAction = (action: EntityAction) => {
        this.action = action;

        // Hide all sprites
        if (this.attackingSprite) {
            this.attackingSprite.stop();
            this.attackingSprite.visible = false;
        }
        if (this.idleSprite) {
            this.idleSprite.visible = false;
        }
        if (this.movingSprite) {
            this.movingSprite.stop();
            this.movingSprite.visible = false;
        }

        // Show action sprite
        if (action === 'attacking' && this.attackingSprite) {
            this.attackingSprite.play();
            this.attackingSprite.visible = true;
        } else if (action === 'moving' && this.movingSprite) {
            this.movingSprite.play();
            this.movingSprite.visible = true;
        } else if (this.idleSprite) {
            this.idleSprite.visible = true;
        }
    };

    /** Shows the glow */
    showGlow = async () => {
        if (!this.glowSprite) {
            try {
                const texture = await getTexture('glow.png');
                this.glowSprite = new PIXI.Sprite(texture);
                this.addSprite(this.glowSprite);
                this.addChildAt(this.glowSprite, 0);
            } catch (e) {
                console.warn('Failed to load glow texture:', e);
                return;
            }
        }

        // Reset
        this.glowSprite.rotation = 0;
        this.glowSprite.scale.set(0);
        this.glowSprite.visible = true;

        // Get scale
        const scale = (mapSetup.cells.size / this.glowSprite.texture.width) * this.size * 3;

        // Animate
        gsap.to(this.glowSprite, {
            duration: 1,
            ease: 'linear',
            rotation: -2,
        });

        gsap.to(this.glowSprite.scale, {
            duration: 0.5,
            ease: 'power4.out',
            x: scale,
            y: scale,
        });

        gsap.to(this.glowSprite.scale, {
            delay: 0.8,
            duration: 0.2,
            ease: 'power4.in',
            x: 0,
            y: 0,
            onComplete: () => {
                if (this.glowSprite) {
                    this.glowSprite.visible = false;
                }
            },
        });
    };

    /** Tries to a target with a killChance */
    attackTarget = (target?: Entity | null, callback?: CallableFunction) => {
        if (target?.active && !target.destroyed) {
            this.targetEngaged = true;

            // Get kill chance
            const killChance = this.getKillChance();

            // Attack
            this.setAction('attacking');
            this.lookAtTarget(target);

            if (chance(killChance)) {
                this.debugLog(`Killed '${target.name}'`);
                this.kills++;
                this.killsReached = this.kills >= this.killsTarget;
                target.die();
            } else {
                this.debugLog(`Missed '${target.name}'`);
            }

            this.queue(callback, true);
        } else {
            this.queue(callback);
        }
    };

    /** Gets the current killChance based on setup and buffs */
    getKillChance = () => {
        let killChance = this.baseSetup.killChance || 0;
        if (this.category === 'ant') {
            killChance *= this.gameState.entities.ants.buffs.attack + 1;
        } else if (this.category === 'danger') {
            killChance *= 1 - this.gameState.entities.ants.buffs.defense;
        }
        return killChance;
    };

    /** Rotates to face the target */
    lookAtTarget = (target: Entity) => {
        if (this.textureAutoRotate !== false) {
            this.rotation = Math.atan2(target.y - this.y, target.x - this.x) + Math.PI / 2 + random(-0.5, 0.5);
        }
    };

    /** Gets the closest target, with optional maximum distance */
    getClosestTarget = <T extends Entity>(targets: T[], maxDistance?: number) => {
        const reachable = this.getTargetsInRange(targets, maxDistance);
        return reachable.find(target => this.getPathTo(target.cell).length);
    };

    /** Gets the closest target in list of targets in order of preference, with optional maximum distance */
    getClosestPreferredTarget = <T extends Entity>(targets: T[][], maxDistance?: number) => {
        const preferred = targets.find(
            entities => entities.length && entities.some(target => target.active && target.visible && !target.destroyed)
        );
        return preferred ? this.getClosestTarget(preferred, maxDistance) : undefined;
    };

    /** Gets an active target at random */
    getRandomTarget = <T extends Entity>(targets: T[]) => {
        const availableTargets = targets.filter(target => target.active && target.visible && !target.destroyed);
        return getRandom(availableTargets);
    };

    /** Gets the targets within maximum distance */
    getTargetsInRange = <T extends Entity>(targets: T[], maxDistance?: number) => {
        const items = targets
            .filter(target => target.active && target.visible && !target.destroyed)
            .map(target => ({
                distance: this.getDistanceTo(target.cell),
                target,
            }))
            .filter(item => !maxDistance || item.distance <= maxDistance);
        items.sort((a, b) => (a.distance < b.distance ? -1 : 1));
        return items.map(item => item.target);
    };

    getPathGrid = () => {
        return new PF.Grid(this.gameState.gridMatrix);
    };

    /** Gets the path to a cell, with optional offset (cells) */
    getPathTo = (cell: PIXI.Point, offset = 0) => {
        // Get grid
        const grid = this.getPathGrid();
        grid.setWalkableAt(cell.x, cell.y, true);

        // Get path
        const path = pathFinder
            .findPath(this.cell.x, this.cell.y, cell.x, cell.y, grid)
            .map(([x, y]) => createCell(x, y));

        // Check offset and cell blocking
        offset = offset ? Math.max(0, Math.ceil(offset)) : 0;
        if (offset) {
            path.splice(path.length - offset, offset);
        } else if (!this.isReachable(cell)) {
            path.splice(path.length - 1, 1);
        }

        return path;
    };

    /** Gets the direct distance to a cell */
    getDistanceTo = (cell: PIXI.Point) => {
        return hypotenuse(this.cell.x - cell.x, this.cell.y - cell.y);
    };

    /** Gets the amount of steps to a cell */
    getStepsTo = (cell: PIXI.Point | null, path?: PIXI.Point[]) => {
        // Check path
        const p = (cell ? this.getPathTo(cell) : path) || [];
        // Get steps
        return p.reduce((prevSteps, nextCell, index) => {
            // Diagonal
            const prevCell = p[index - 1];
            if (prevCell && nextCell.x !== prevCell.x && nextCell.y !== prevCell.y) {
                return prevSteps + Math.sqrt(2);
            }

            // Horizontal or vertical
            return prevSteps + 1;
        }, 0);
    };

    /** Gets the current movingSpeed based on setup */
    getMovingSpeed = () => {
        return this.baseSetup.movingSpeed || 1;
    };

    /** Gets the current wanderDistance based on setup */
    getWanderDistance = () => {
        return this.baseSetup.wanderDistance || 0;
    };

    /** Checks to see if it's close to cell within a maximum distance */
    isCloseTo = (cell: PIXI.Point, maxDistance = 0.5) => {
        return this.getDistanceTo(cell) <= Math.max(1, this.size) / 2 + maxDistance;
    };

    /** Checks to see if it's close to target within a maximum distance */
    isCloseToTarget = (target?: Entity | null, maxDistance = 0.5) => {
        // Check existence
        if (!target?.active || target.destroyed) {
            return false;
        }

        // Check if close to
        return this.isCloseTo(target.cell, Math.max(1, target.size) / 2 + maxDistance);
    };

    /** Checks to see if entity is in view of the user */
    isInView = () => {
        const { view } = this.map;
        return (
            !this.gameState.fog[this.cellName] &&
            this.cell.x >= view.left &&
            this.cell.x <= view.right &&
            this.cell.y >= view.top &&
            this.cell.y <= view.bottom
        );
    };

    /** Checks to see if a cell is not blocked */
    isReachable = (cell: PIXI.Point) => {
        return !this.gameState.gridMatrix[cell.y][cell.x];
    };

    /** Moves to cell, with optional offset */
    moveTo = (cell: PIXI.Point, offset = 0, callback?: CallableFunction) => {
        // Check distance
        if (this.cell.x === cell.x && this.cell.y === cell.y) {
            this.queue(callback);
            return;
        }

        // Get path to destinations
        const path = this.getPathTo(cell, offset);
        if (path.length < 2) {
            this.queue(callback);
            return;
        }

        // Store departure and destination
        this.moveDeparture = this.cell.clone();
        this.moveDestination = cell.clone();

        // Get duration
        const movingSpeed = this.getMovingSpeed();
        const steps = this.getStepsTo(null, path);
        const duration = steps / movingSpeed;

        // Convert path cells to positions
        path.forEach((cell, i) => {
            const n = (i / path.length) * 0.2;
            cell.x = (random(-n, n) + cell.x + 0.5) * mapSetup.cells.size;
            cell.y = (random(-n, n) + cell.y + 0.5) * mapSetup.cells.size;
        });

        // Move to destination at cells per second
        this.setAction('moving');
        gsap.to(this, {
            duration,
            ease: 'linear',
            motionPath: {
                autoRotate: this.textureAutoRotate === false ? false : Math.PI / 2,
                curviness: random(0.5, 1),
                path,
                useRadians: true,
            },
            onComplete: () => {
                this.stop();
                this.queue(callback);
            },
            onUpdate: this.updateCell,
        });
    };

    /** Moves to a cell next to a target */
    moveToTarget = (target: Entity, callback?: CallableFunction) => {
        const offset = Math.floor(Math.max(1, target.size) / 2 + Math.max(1, this.size) / 2);
        this.moveTo(target.cell, offset, callback);
    };

    /** Moves instantly to a cell */
    placeAt = (cell: PIXI.Point) => {
        this.x = (cell.x + 0.5) * mapSetup.cells.size;
        this.y = (cell.y + 0.5) * mapSetup.cells.size;
        this.updateCell();
    };

    /** Updates current cell based on position */
    updateCell = () => {
        // Update cell
        this.cell.x = clamp(Math.floor(this.x / mapSetup.cells.size), 0, mapSetup.cells.wide - 1);
        this.cell.y = clamp(Math.floor(this.y / mapSetup.cells.size), 0, mapSetup.cells.wide - 1);
        const cellName = `${this.cell.x},${this.cell.y}`;
        if (this.cellName !== cellName) {
            this.cellName = cellName;
            this.soundPosition = null;
            this.onMove();
        }

        // Check following
        if (this.following) {
            this.map.centerPosition(this.position);
        }
    };

    /** Event for when moving */
    onMove = () => {
        // Handle onMove
    };

    /** Moves towards a cell with attraction (0-1) */
    wanderTo = (cell: PIXI.Point, attraction = 0, callback?: CallableFunction) => {
        // Get offset based on attraction
        const angle = random(0, Math.PI * 2);
        const distance = random(1, Math.max(1, (1 - attraction) * this.getWanderDistance()));
        const offset = createCell();
        offset.x = Math.sin(angle) * distance;
        offset.y = Math.cos(angle) * distance;

        // Get destination
        const destination = createCell();
        destination.x = clamp(
            Math.round(attraction * (cell.x - this.cell.x) + this.cell.x + offset.x),
            0,
            mapSetup.cells.wide - 1
        );
        destination.y = clamp(
            Math.round(attraction * (cell.y - this.cell.y) + this.cell.y + offset.y),
            0,
            mapSetup.cells.wide - 1
        );

        // Check reachability to move to destination, otherwise try again
        if (this.isReachable(destination)) {
            this.moveTo(destination, 0, callback);
        } else {
            this.queue(() => {
                this.wanderTo(cell, attraction, callback);
            });
        }
    };

    /** Moves to target position or a corner cell and removes itself */
    wanderOff = (x?: number, y?: number) => {
        this.stop();
        const cell = createCell(x, y);
        this.moveTo(cell, 0, this.remove);
    };
}
